import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {
    CHANNEL_INFO_BY_CHANNEL,
    CHANNEL_TYPE_ANALYTICS,
    CHANNEL_TYPE_ECOMMERCE,
    CHANNEL_TYPE_EMAIL_MARKETING,
    CHANNEL_TYPE_PAID_MEDIA,
    CHANNEL_TYPE_SEO,
    ChannelsHelper,
    MERCADO_ADS_CHANNEL,
    SHEET_CHANNEL,
} from '../../channels/channels';
import {missingExpectedPlatforms} from './expectedPlatforms';
import {SendConnectionLinkModal} from '../../connection_links/SendConnectionLinkModal';

// Rediseño 2026-07-06 (feedback fede): listar el catálogo ENTERO de integraciones no aportaba —
// y a un cliente de generación de leads no tiene sentido ofrecerle Shopify/Tiendanube. Ahora:
// conectadas siempre visibles; de las no conectadas solo un puñado de SUGERENCIAS relevantes al
// tipo de negocio del cliente (business_type del perfil); el resto (incl. ignoradas) queda
// detrás de "Ver todas las integraciones". Nada desaparece — solo deja de gritar.
const SUGGESTED_LIMIT = 3;

// Tipos de negocio donde una tienda/email de ecommerce no aplica.
const NON_ECOMMERCE_BUSINESS_TYPES = new Set(['LEAD_GEN', 'APP_INSTALL', 'BRAND_AWARENESS']);
const ECOMMERCE_ONLY_CHANNEL_TYPES = new Set([CHANNEL_TYPE_ECOMMERCE, CHANNEL_TYPE_EMAIL_MARKETING]);

// Orden de sugerencia: primero lo que suma a cualquier cliente (paid media, GA4, GSC).
const CHANNEL_TYPE_PRIORITY = {
    [CHANNEL_TYPE_PAID_MEDIA]: 0,
    [CHANNEL_TYPE_ANALYTICS]: 1,
    [CHANNEL_TYPE_SEO]: 2,
    [CHANNEL_TYPE_ECOMMERCE]: 3,
    [CHANNEL_TYPE_EMAIL_MARKETING]: 4,
};
// Canales de nicho que nunca compiten por un lugar de sugerencia.
const CHANNEL_PRIORITY_OVERRIDE = {
    [SHEET_CHANNEL]: 99,
};

/**
 * `renderAccountsDetail(channel, accounts)` es OPCIONAL y existe para el Panel del cliente (UX 2.1),
 * que necesita mostrar el límite diario de cada cuenta ahí mismo (card Fa9hRD9I: Monitoreo no se
 * puede eliminar hasta que el panel cubra lo que hoy sólo vive en esa pantalla). Quien no la pasa
 * —la ficha clásica— renderiza exactamente lo de siempre: el resumen "Nombre (ID: …)" o "N cuentas".
 * Si el renderer devuelve `null` para un canal, esa fila también cae al resumen de siempre.
 *
 * Se hace así, y no con una card propia del panel, porque las dos pantallas tienen que contestar lo
 * mismo sobre qué está conectado: dos serializaciones del mismo concepto se desincronizan sin que
 * nadie se entere.
 *
 * `renderMissingNotice(missing)` también es OPCIONAL y también es del Panel: dibuja arriba de la
 * lista el aviso de las fuentes esperables que faltan (`expectedPlatforms.js`). La ficha clásica no
 * la pasa y no muestra aviso.
 */
export function PlatformsBox({clientId, clientAdAccounts, clientStores, channels, ignoredChannels,
                              businessType, onConnectChannel, onIgnoreChannel, onUnignoreChannel,
                              readOnly, renderAccountsDetail, renderMissingNotice, manageLabel, onAddAccount, id,
                              guideAnchor = {}}) {
    const {t} = useTranslation();
    const [showAll, setShowAll] = useState(false);
    // Mercado Ads lo autoriza casi siempre el vendedor (sólo su cuenta principal puede): desde la ficha,
    // con el cliente ya elegido, se le manda el link.
    const [sendLinkOpen, setSendLinkOpen] = useState(false);

    if (!channels || channels.length === 0) return null;

    // Build map of accounts by channel
    const accountsByChannel = {};
    (clientAdAccounts || []).forEach(acc => {
        const ch = acc.channel;
        if (!accountsByChannel[ch]) accountsByChannel[ch] = [];
        accountsByChannel[ch].push(acc);
    });
    (clientStores || []).forEach(store => {
        const ch = store.channel;
        if (!accountsByChannel[ch]) accountsByChannel[ch] = [];
        accountsByChannel[ch].push(store);
    });

    // Build ignored channels map
    const ignoredMap = {};
    (ignoredChannels || []).forEach(ic => {
        ignoredMap[ic.channel] = ic;
    });

    // Only show channels the agency has enabled. La familia de rentabilidad (Escalafy) no es una plataforma
    // de la que se vinculen cuentas acá: alimenta la economía del cliente, que tiene su propia card.
    const visibleChannels = channels.filter(ch => CHANNEL_INFO_BY_CHANNEL[ch] && !ChannelsHelper.channelIsProfitability(ch));

    const connected = [];
    const unconnected = [];
    const ignored = [];

    visibleChannels.forEach(ch => {
        const hasAccounts = accountsByChannel[ch] && accountsByChannel[ch].length > 0;
        const isIgnored = !!ignoredMap[ch];

        if (hasAccounts) {
            connected.push(ch);
        } else if (isIgnored) {
            ignored.push(ch);
        } else {
            unconnected.push(ch);
        }
    });

    const isRelevantForBusiness = (ch) => {
        const type = CHANNEL_INFO_BY_CHANNEL[ch].channelType;
        if (NON_ECOMMERCE_BUSINESS_TYPES.has(businessType) && ECOMMERCE_ONLY_CHANNEL_TYPES.has(type)) {
            return false;
        }
        return true;
    };

    const channelPriority = (ch) => {
        if (CHANNEL_PRIORITY_OVERRIDE[ch] !== undefined) return CHANNEL_PRIORITY_OVERRIDE[ch];
        const type = CHANNEL_INFO_BY_CHANNEL[ch].channelType;
        return CHANNEL_TYPE_PRIORITY[type] !== undefined ? CHANNEL_TYPE_PRIORITY[type] : 50;
    };

    // Lo que el cliente DEBERÍA tener y no tiene (GA4, la tienda si vende online) va primero entre las
    // sugeridas: con 3 lugares, un cliente con varias redes sin conectar dejaba a GA4 detrás de «Ver más».
    const missingExpected = missingExpectedPlatforms({channels: visibleChannels, accountsByChannel,
                                                      ignoredChannels, businessType});
    const expectedSet = new Set(missingExpected.map(m => m.channel).filter(Boolean));
    const suggestionRank = (ch) => (expectedSet.has(ch) ? -1 : channelPriority(ch));

    const suggested = unconnected
        .filter(isRelevantForBusiness)
        .sort((a, b) => suggestionRank(a) - suggestionRank(b))
        .slice(0, SUGGESTED_LIMIT);
    const suggestedSet = new Set(suggested);
    const collapsed = unconnected.filter(ch => !suggestedSet.has(ch));
    const hiddenCount = collapsed.length + ignored.length;

    const formatAccountInfo = (ch) => {
        const accounts = accountsByChannel[ch] || [];
        if (accounts.length === 1) {
            const acc = accounts[0];
            const name = acc.name || '';
            const platformId = acc.account_id || '';
            return name ? `${name} (ID: ${platformId})` : `ID: ${platformId}`;
        }
        const names = accounts.map(a => a.name || `ID: ${a.account_id}`).join('\n');
        return {
            text: t('control_central.platforms_accounts_count', {count: accounts.length}),
            title: names
        };
    };

    const formatIgnoredInfo = (ch) => {
        const ic = ignoredMap[ch];
        if (!ic) return null;
        if (ic.ignored_by_name && ic.ignored_at) {
            const date = new Date(ic.ignored_at);
            const formatted = date.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit'});
            return t('control_central.platforms_ignored_by', {user: ic.ignored_by_name, date: formatted, interpolation: {escapeValue: false}});
        }
        return t('control_central.platforms_ignored');
    };

    // «Vincular», no «Conectar» (6zUFUONB, jaM5imSj): el botón suma al cliente una cuenta que la
    // agencia YA tiene. Conectar una plataforma es otra operación, de la agencia, y la gente las
    // confundía: iba a «conectar» pensando que vinculaba.
    const linkLabel = (channel) => (CHANNEL_INFO_BY_CHANNEL[channel].channelType === CHANNEL_TYPE_ECOMMERCE
        ? t('control_central.platforms_link_store')
        : t('control_central.platforms_link_account'));

    const renderConnectableRow = (channel) => {
        const info = CHANNEL_INFO_BY_CHANNEL[channel];
        return (
            <div key={channel} className="platforms-row">
                <div className="platforms-channel">
                    <ChannelLogo channel={channel} height={18} width={18} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                    <span className="platforms-channel-name">{info.Spanish}</span>
                </div>
                <div className="platforms-actions">
                    <button className="platforms-badge-connect" onClick={() => onConnectChannel(channel)}>
                        {linkLabel(channel)}
                    </button>
                    {channel === MERCADO_ADS_CHANNEL && (
                        <button type="button" className="platforms-badge-connect -subtle" data-testid="platforms-mercado-ads-send-link"
                                onClick={() => setSendLinkOpen(true)}>
                            {t('mercado_ads_link.choose.send_link')}
                        </button>
                    )}
                    <button className="platforms-badge-ignore" onClick={() => onIgnoreChannel(channel)}>
                        {t('control_central.platforms_ignore')}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="box platforms-box" id={id} {...guideAnchor}>
            <div className="platforms-header">
                {/* «Fuentes de datos (N)»: el mismo nombre que la línea y que el «Vincular fuente» de
                    al lado (el mockup del Panel dice «Fuentes conectadas», pero acá también hay
                    canales SIN conectar). N son las cuentas y tiendas ya vinculadas al cliente. */}
                <h3 className="platforms-title">
                    {t('control_central.platforms_title')}{' '}
                    <span className="platforms-count">
                        ({(clientAdAccounts || []).length + (clientStores || []).length})
                    </span>
                </h3>
                {!readOnly && (
                    <Link className="platforms-manage-link" to={`/client/${clientId}/conections`}>
                        {/* El panel 2.1 lo llama "Vincular fuente" (mockup de Pablo, card Fa9hRD9I);
                            la ficha clásica sigue diciendo "Gestionar". */}
                        {manageLabel || t('control_central.platforms_manage')} &rarr;
                    </Link>
                )}
            </div>
            {!readOnly && renderMissingNotice && missingExpected.length > 0 && renderMissingNotice(missingExpected)}
            <div className="platforms-list">
                {/* Connected channels */}
                {connected.map(channel => {
                    const info = CHANNEL_INFO_BY_CHANNEL[channel];
                    const accountInfo = formatAccountInfo(channel);
                    const isMultiple = typeof accountInfo === 'object';
                    const accountsDetail = renderAccountsDetail
                        ? renderAccountsDetail(channel, accountsByChannel[channel] || [])
                        : null;

                    return (
                        <div key={channel} className={`platforms-row${accountsDetail ? ' -stacked' : ''}`}>
                            <div className="platforms-channel">
                                <ChannelLogo channel={channel} height={18} width={18} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                                <span className="platforms-channel-name">{info.Spanish}</span>
                                {/* Sumar una SEGUNDA cuenta de un canal que ya tiene una (jaM5imSj):
                                    sin esto, el único camino era salir a la pantalla de cuentas. */}
                                {!readOnly && onAddAccount && (
                                    <button type="button" className="platforms-badge-connect -subtle platforms-add-another"
                                            onClick={() => onAddAccount(channel)}>
                                        {t('control_central.platforms_link_another')}
                                    </button>
                                )}
                            </div>
                            {accountsDetail || (
                                <span className="platforms-account-info" title={isMultiple ? accountInfo.title : undefined}>
                                    {isMultiple ? accountInfo.text : accountInfo}
                                </span>
                            )}
                        </div>
                    );
                })}

                {/* Sugerencias (no conectadas relevantes al negocio) */}
                {!readOnly && suggested.map(renderConnectableRow)}

                {/* El resto, colapsado: no conectadas de baja relevancia + ignoradas */}
                {!readOnly && hiddenCount > 0 && !showAll && (
                    <button type="button" className="platforms-toggle" onClick={() => setShowAll(true)}>
                        {t('control_central.platforms_show_all', {count: hiddenCount})}
                    </button>
                )}
                {!readOnly && showAll && (
                    <>
                        {collapsed.map(renderConnectableRow)}
                        {ignored.map(channel => {
                            const info = CHANNEL_INFO_BY_CHANNEL[channel];
                            const ignoredInfo = formatIgnoredInfo(channel);

                            return (
                                <div key={channel} className="platforms-row -ignored">
                                    <div className="platforms-channel">
                                        <ChannelLogo channel={channel} height={18} width={18} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                                        <span className="platforms-channel-name">{info.Spanish}</span>
                                        {ignoredInfo && <span className="platforms-ignored-info">{ignoredInfo}</span>}
                                    </div>
                                    <button className="platforms-badge-connect -subtle" onClick={() => onUnignoreChannel(channel)}>
                                        {linkLabel(channel)}
                                    </button>
                                </div>
                            );
                        })}
                        <button type="button" className="platforms-toggle" onClick={() => setShowAll(false)}>
                            {t('control_central.platforms_show_less')}
                        </button>
                    </>
                )}
            </div>
            {!readOnly && visibleChannels.includes(MERCADO_ADS_CHANNEL) && (
                <SendConnectionLinkModal show={sendLinkOpen} onHide={() => setSendLinkOpen(false)}
                                         channel={MERCADO_ADS_CHANNEL} agencyClientId={clientId}/>
            )}
        </div>
    );
}
